<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Headphones } from 'lucide-vue-next'
import { useCSKH } from '@99fun-agent/hooks'

const fabPosition = ref({ x: 0, y: 0 })
const isDragging = ref(false)
let offset = { x: 0, y: 0 }
// 记录初始位置，用于区分点击和拖拽
let startPosition = { x: 0, y: 0 }
// 移动距离阈值，超过此值视为拖拽而不是点击
const DRAG_THRESHOLD = 5

onMounted(() => {
  const updatePosition = () => {
    const screenWidth = window.innerWidth
    // 根据屏幕宽度计算按钮位置
    if (screenWidth >= 1024) {
      // 大屏幕：右侧固定位置
      fabPosition.value = {
        x: screenWidth - 80,
        y: window.innerHeight - 180,
      }
    } else {
      // 小屏幕：右侧固定位置
      fabPosition.value = {
        x: screenWidth - 80,
        y: window.innerHeight - 180,
      }
    }
  }

  updatePosition()
  window.addEventListener('resize', updatePosition)
})

const startDrag = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true
  const clientX = 'touches' in e ? e.touches[0]!.clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0]!.clientY : e.clientY

  // 记录初始位置
  startPosition = { x: clientX, y: clientY }
  offset = { x: clientX - fabPosition.value.x, y: clientY - fabPosition.value.y }

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('touchmove', onDrag, { passive: false })
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchend', stopDrag)
}

const onDrag = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return
  if ('touches' in e) e.preventDefault()
  const clientX = 'touches' in e ? e.touches[0]!.clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0]!.clientY : e.clientY
  fabPosition.value = {
    x: Math.min(Math.max(10, clientX - offset.x), window.innerWidth - 60),
    y: Math.min(Math.max(10, clientY - offset.y), window.innerHeight - 60),
  }
}

const stopDrag = (e: MouseEvent | TouchEvent) => {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('touchmove', onDrag)

  // 计算移动距离
  const clientX = 'touches' in e ? e.changedTouches[0]!.clientX : e.clientX
  const clientY = 'touches' in e ? e.changedTouches[0]!.clientY : e.clientY

  const moveDistance = Math.sqrt(
    Math.pow(clientX - startPosition.x, 2) + Math.pow(clientY - startPosition.y, 2),
  )

  // 如果移动距离小于阈值，视为点击，打开百度
  if (moveDistance < DRAG_THRESHOLD) {
    onClick()
  }
}
const { openAgentCSKH } = useCSKH()
const onClick = () => {
  //打开客服
  openAgentCSKH()
}
</script>

<template>
  <div
    class="fixed z-[999] touch-none select-none"
    :style="{ left: fabPosition.x + 'px', top: fabPosition.y + 'px' }"
    @mousedown="startDrag"
    @touchstart="startDrag"
  >
    <div
      class="w-14 h-14 bg-gradient-to-tr from-[#A834FF] to-[#2B6DFF] rounded-full shadow-2xl flex items-center justify-center text-white active:scale-95 transition-all duration-200 animate-breath"
      :class="{
        'cursor-grabbing': isDragging,
        'cursor-grab hover:scale-105': !isDragging,
      }"
    >
      <Headphones :size="24" />
    </div>
  </div>
</template>

<style scoped>
@keyframes breath {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 15px rgba(168, 52, 255, 0.4);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 25px rgba(43, 109, 255, 0.6);
  }
}
.animate-breath {
  animation: breath 3s ease-in-out infinite;
}
</style>
